import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';

import { Box, TextField as MuiTextField, Typography } from '@mui/material';
import { spacing } from '@mui/system';
import { LockReset } from '@mui/icons-material';
import styled from '@emotion/styled';

import { useAuthStore } from '../../stores/';
import { userMessages } from '../../utils/userMessages';
import { resetPassword } from '../../apis/authApi';
import { useUserMessageStore } from '../../stores';

import Center from '../../components/common/Center';
import GuestGuard from '../../components/guards/GuestGuard';
import { confirmPassword, password } from '../../validators/passwordValidators';
import MessageAlert from '../../components/common/MessageAlert';
import SubmitButton from '../../components/common/SubmitButton';

const TextField = styled(MuiTextField)(spacing);
const Button = styled(SubmitButton)(spacing);

const LockIcon = styled(LockReset)`
  width: 60px;
  height: 60px;
  margin: 0 auto ${(props) => props.theme.spacing(5)};
`;

function PasswordReset() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const token = searchParams.get('token');

  const navigate = useNavigate();

  const signOutLocal = useAuthStore.use.signOutLocal();
  const setUserMessage = useUserMessageStore.use.setUserMessage();

  useEffect(() => {
    signOutLocal();
  }, []);

  return (
    <>
      <GuestGuard>
        <Helmet title="Password Reset" />
        <Center>
          <LockIcon color="primary"></LockIcon>
        </Center>
        <Typography component="h1" variant="h3" align="center" gutterBottom>
          Password Reset
        </Typography>
        <Typography component="h2" variant="subtitle1" align="center">
          Enter your new password below
        </Typography>
        <Box mt={4}>
          <Formik
            initialValues={{
              password: '',
              confirmPassword: '',
              submit: false,
            }}
            validationSchema={Yup.object().shape({
              password: password,
              confirmPassword: confirmPassword,
            })}
            onSubmit={async (values, { setStatus, setSubmitting }) => {
              try {
                if (!token || !email) {
                  // noinspection ExceptionCaughtLocallyJS
                  throw new Error(userMessages.Invalid_Password_Reset);
                }
                await resetPassword({ token: token, email: email, password: values.password });
                setUserMessage(userMessages.SignUp_Success, 'success');

                navigate('/auth/sign-in');
              } catch {
                setStatus({ success: false });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
              <form noValidate onSubmit={handleSubmit}>
                <MessageAlert></MessageAlert>
                <TextField type="text" name="email" autoComplete="email" value={email} disabled={true} fullWidth my={3} />
                <TextField
                  type="password"
                  name="password"
                  label="Password"
                  autoComplete="new-password"
                  value={values.password}
                  inputProps={{
                    maxLength: 50,
                  }}
                  error={Boolean(touched.password && errors.password)}
                  fullWidth
                  helperText={touched.password && errors.password}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={3}
                />
                <TextField
                  type="password"
                  autoComplete="new-password"
                  name="confirmPassword"
                  label="Confirm password"
                  value={values.confirmPassword}
                  inputProps={{
                    maxLength: 50,
                  }}
                  error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                  fullWidth
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={3}
                />
                <Button my={3} text="Reset password" isSubmitting={isSubmitting}></Button>
              </form>
            )}
          </Formik>
        </Box>
      </GuestGuard>
    </>
  );
}

export default PasswordReset;
